import { ErrorSeverity } from '../shared/types';
import StatusCode from 'status-code-enum';
import { Timer } from '@lib/shared';
import { sources } from '../api/sources';
import { IProcessPaymentWithManifestRequest, TransactionStatus } from '@ace-clublabs/payment-types';
import { IPayWithStoredPaymentRequest } from '../types';
import LogCannon from '@ace-clublabs/log-cannon';

const previousTransactionTimedOut = 'previous-transaction-timed-out';

export class ProcessPaymentHelper {
  static waitForCompleteTransaction = async (id: string) => {
    const errorStatuses = [StatusCode.ClientErrorNotFound, StatusCode.ServerErrorInternal];
    const error = { type: ErrorSeverity.HARD };
    const MAX = 30;
    let i = 0;
    while (i < MAX) {
      const { statusCode, transactionStatus, message, freedomPayReasonCode, code } =
        (await sources.getTransactionStatus(id)) || {};
      if (statusCode === StatusCode.SuccessOK) {
        LogCannon.blast(`Transaction status polling attempts`, { pollingAttempted: i });
        return transactionStatus === TransactionStatus.Failed
          ? Promise.reject({ transactionStatus, type: ErrorSeverity.SOFT, message, freedomPayReasonCode, code })
          : Promise.resolve({ transactionStatus });
      }
      if (errorStatuses.includes(statusCode)) return Promise.reject(error);
      await Timer.sleep(1000);
      i++;
    }
    LogCannon.error(`Polling timed out. No status received within the specified time limit. Transaction ID: ${id}`);
    return Promise.reject(error);
  };

  static processPayment = async (
    request: IProcessPaymentWithManifestRequest
  ): Promise<{ transactionId: string; status?: TransactionStatus }> => {
    const { transactionId, transactionProcessStatus } = await sources.processPayment(request);
    let status;

    if (transactionProcessStatus === previousTransactionTimedOut) {
      LogCannon.error(`Transaction timed out during processPayment flow. Transaction ID: ${transactionId}`);
      throw { type: ErrorSeverity.HARD };
    }
    if (transactionProcessStatus !== previousTransactionTimedOut) {
      const { transactionStatus } = await ProcessPaymentHelper.waitForCompleteTransaction(transactionId);
      status = transactionStatus;
    }

    return Promise.resolve({ status, transactionId });
  };

  static payWithStoredPayment = async (
    request: IPayWithStoredPaymentRequest
  ): Promise<{ transactionId: string; status?: TransactionStatus }> => {
    const { transactionId, transactionProcessStatus } = await sources.payWithStoredPayment(request);
    let status;

    if (transactionProcessStatus === previousTransactionTimedOut) {
      LogCannon.error(`Transaction timed out during payWithStoredPayment flow. Transaction ID: ${transactionId}`);
      throw { type: ErrorSeverity.HARD };
    }

    if (transactionProcessStatus !== previousTransactionTimedOut) {
      const { transactionStatus } = await ProcessPaymentHelper.waitForCompleteTransaction(transactionId);
      status = transactionStatus;
    }

    return Promise.resolve({ status, transactionId });
  };

  //used for async digital wallet processing
  static startDigitalWalletPayment = async (request: IProcessPaymentWithManifestRequest): Promise<string> => {
    const { transactionId, transactionProcessStatus } = await sources.processPayment(request);

    if (transactionProcessStatus === previousTransactionTimedOut) {
      LogCannon.error(`Transaction timed out during startDigitalWalletPayment flow. Transaction ID: ${transactionId}`);
      throw { type: ErrorSeverity.HARD };
    }

    return transactionId;
  };
}
